import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription, Observable, zip, of } from 'rxjs';
import { take, tap, map } from 'rxjs/operators';

import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';

import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { ShiftStatus } from '@shared/models/worked-shift';
import { SubinventoryType } from '@shared/models/subinventory';

import { ProductService } from '../../services/product.service';
import { PalletReclassificationService } from '../../services/pallet-reclassification.service';
import { ShiftContextService } from '../../services/shift-context.service';

import { PalletReclassificationItem } from '../../models/pallet-reclassification';

@Component({
    selector: 'app-pallet-reclassification',
    templateUrl: './pallet-reclassification.component.html',
    styleUrls: ['./pallet-reclassification.component.scss']
})
export class PalletReclassificationComponent implements OnInit, OnDestroy {

    ShiftStatus = ShiftStatus;

    subscriptions: Subscription[];
    startupSubscription: Subscription;

    subinventories: SelectItem[] = [];
    palletReclassification: PalletReclassificationItem;
    shiftContextId: number;

    addForm: UntypedFormGroup = new UntypedFormGroup({
        siteSubinventoryId: new UntypedFormControl(null, Validators.required),
        fromProductId: new UntypedFormControl(null, Validators.required),
        toProductId: new UntypedFormControl(null, Validators.required),
        quantity: new UntypedFormControl(0, Validators.required),
        referenceNumber: new UntypedFormControl('', Validators.maxLength(20))
    });
    newPalletReclassificationItem: boolean;
    palletReclassificationItems: PalletReclassificationItem[];
    // clonedPalletReclassificationItems: { [s: string]: PalletReclassificationItem; } = {};
    palletReclassificationItemCloned: PalletReclassificationItem = null;
    loadingPost = false;
    loadingDelete: number = null;
    defaultValuefields = { siteSubinventoryId: null, fromProductId: null, toProductId: null, quantity: 0, referenceNumber: '' };

    formProducts: Observable<SelectItem[]>;

    anyItemsAreClosed: boolean = false;

    readonly _reclassificationNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Pallet Reclassification Item',
        detail: 'Fields are required.'
    };

    readonly _reclassificationCreatedMsg: Message = {
        severity: 'success',
        summary: 'Pallet Reclassification Item Created',
        detail: ''
    };

    readonly _reclassificationNotUpdatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Update Pallet Reclassification Item',
        detail: 'Fields are required.'
    };

    readonly _reclassificationUpdatedMsg: Message = {
        severity: 'success',
        summary: 'Pallet Reclassification Item Updated',
        detail: ''
    };

    readonly _reclassificationDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Pallet Reclassification Item Deleted',
        detail: ''
    };

    constructor(
        private productService: ProductService,
        private messageService: MessageService,
        private palletReclassificationService: PalletReclassificationService,
        private shiftContextService: ShiftContextService,
        private activityService: ActivityBlockingService) {}

    ngOnInit() {
        this.palletReclassificationService.load();
        this.palletReclassificationItems = [];
        const initHandle = this.activityService.StartWorking();

        const validSubinventoryTypes = [
            SubinventoryType.ChepPlantPallets,
            SubinventoryType.ChepPalletStorage,
            SubinventoryType.VendorOnPremise,
            SubinventoryType.AssetRecovery,
            SubinventoryType.PecoPallets,
            SubinventoryType.TpmProduction
        ];

        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.shiftContextId = context.id;
            this.subinventories = context.site.subinventories
                .filter(s => s.isActive
                        && (validSubinventoryTypes.includes(s.subinventoryType)))
                .map(s => ({ value: s.id, label: s.name }));

            if (context.status !== ShiftStatus.Open)
                this.addForm.disable();
            else
                this.addForm.enable();
        }));

        const palletReclassificationObservable = this.palletReclassificationService.CurrentPalletReclassificationList
            .pipe(tap(reclassList => {
                this.palletReclassificationItems = reclassList;
                this.anyItemsAreClosed = reclassList.some(r => r.workedShiftStatus !== ShiftStatus.Open);
            }));

        this.formProducts = this.productService.ProductsForSubinventory(this.addForm.controls.siteSubinventoryId.valueChanges)
            .pipe(map(products => products.map(p => ({ value: p.id, label: p.label }))));

        this.subscriptions = [
            siteObservable.subscribe(_ => { }),
            palletReclassificationObservable.subscribe(_ => { })
        ];

        this.startupSubscription = zip(
            siteObservable,
            palletReclassificationObservable)
            .pipe(take(1)).subscribe(_vals => {
                this.activityService.FinishWorking(initHandle);
            });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.palletReclassificationService.unload();
    }

    getProductList(siteSubinventoryId: number): Observable<SelectItem[]> {
        return this.productService.ProductsForSubinventory(of(siteSubinventoryId))
            .pipe(map(products => products.map(p => ({ value: p.id, label: p.label }))));
    }

    savePalletReclassification() {
        if (!this.addForm.valid) {
            this.addForm.markAllAsTouched();
            this.messageService.add(this._reclassificationNotCreatedMsg);
        } else {
            this.loadingPost = true;
            this.enableDisableFields(this.loadingPost);
            this.palletReclassification = { ...this.addForm.value };
            this.palletReclassification.workedShiftId = this.shiftContextId;
            this.palletReclassificationService.createReclass(this.palletReclassification).subscribe(_ => {
                this.addForm.reset(this.defaultValuefields);
                this.messageService.add(this._reclassificationCreatedMsg);
                this.loadingPost = false;
                this.enableDisableFields(this.loadingPost);
            }, _error => {
                this.loadingPost = false;
                this.enableDisableFields(this.loadingPost);
            });
        }
    }

    cloneRowData(entry: PalletReclassificationItem) {
        this.palletReclassificationItemCloned = JSON.parse(JSON.stringify(entry));
    }

    onChangePalletReclassificationField(entry: PalletReclassificationItem) {
        if (!entry.quantity) {
            entry.quantity = 0;
        }

        if (!entry.referenceNumber) {
            entry.referenceNumber = '';
        }

        this.updatePalletReclassificationEntry(entry);
    }

    updatePalletReclassificationEntry(palletReclassificationItem: PalletReclassificationItem) {
        this.palletReclassification = { ...palletReclassificationItem };
        // this.palletReclassification = Object.assign({}, palletReclassificationItem);
        this.palletReclassification.workedShiftId = this.shiftContextId;
        this.palletReclassificationService.updateReclass(palletReclassificationItem).subscribe(_data => {
            this.messageService.add(this._reclassificationUpdatedMsg);
        }, _error => {
            let count = 0;
            this.palletReclassificationItems.forEach(entry => {
                if (entry.id === palletReclassificationItem.id) {
                    this.palletReclassificationItems[count] = this.palletReclassificationItemCloned;
                }
                count++;
            });
        });
    }

    onPalletReclassificationRowDelete(palletReclassificationItem: PalletReclassificationItem) {
        this.loadingDelete = palletReclassificationItem.id;
        this.palletReclassificationService.deleteReclass(palletReclassificationItem.id).subscribe(() => {
            this.addForm.reset(this.defaultValuefields);
            this.messageService.add(this._reclassificationDeletedMsg);
            this.loadingDelete = null;
        }, _error => {
            this.loadingDelete = null;
        });
    }

    enableDisableFields(flag: boolean) {
        if (flag) {
            this.addForm.controls.siteSubinventoryId.disable();
            this.addForm.controls.fromProductId.disable();
            this.addForm.controls.toProductId.disable();
            this.addForm.controls.quantity.disable();
            this.addForm.controls.referenceNumber.disable();
        } else {
            this.addForm.controls.siteSubinventoryId.enable();
            this.addForm.controls.fromProductId.enable();
            this.addForm.controls.toProductId.enable();
            this.addForm.controls.quantity.enable();
            this.addForm.controls.referenceNumber.enable();
        }
    }

    rowTrackBy(_i: number, row: PalletReclassificationItem) { return row.id; }
}
